import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetContractTemplateByIdQuery,
  useUpdateContractTemplateMutation,
} from '../Service/contractTemplatesApi';
import { useUploadContractTemplateLogoMutation } from '../Service/extendedContractTemplatesApi';
import { ContractTemplatesRoutes } from '../../../Router/path';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoIcon from '@mui/icons-material/Photo';
import { CircularProgress, Typography } from '@mui/material';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { contractTemplateFields } from '../fields';
import { contractTemplateInitialValues } from '../initialValues';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import UploadImagesForm from '../../Shared/Components/UploadImagesForm/UploadImagesForm';
import ContractTemplateLogoGallery from '../Components/ContractTemplateLogoGallery';
import { ContractTemplate } from '../Types/contractTemplatesTypes';

const EditContractTemplate = () => {
  const { contractTemplateId } = useParams<{ contractTemplateId: string }>();
  const templateId = contractTemplateId ? parseInt(contractTemplateId) : 0;

  // API Hooks
  const {
    data: contractTemplate,
    isLoading,
    error,
  } = useGetContractTemplateByIdQuery(templateId, {
    skip: !templateId,
  });
  const [updateContractTemplate, updateStatus] = useUpdateContractTemplateMutation();
  const [uploadLogo, uploadLogoStatus] = useUploadContractTemplateLogoMutation();

  // Local state
  const [logoUrl, setLogoUrl] = useState<string>('');

  // Ustaw logoUrl gdy dane są załadowane
  useEffect(() => {
    if (contractTemplate?.logoUrl) {
      setLogoUrl(contractTemplate.logoUrl);
    }
  }, [contractTemplate]);

  // Przygotuj dane początkowe dla formularza
  const initialData = useMemo((): ContractTemplate => {
    return contractTemplate
      ? {
          ...contractTemplate,
        }
      : contractTemplateInitialValues;
  }, [contractTemplate]);

  const entityForm = useEntityForm<ContractTemplate>({
    initialData,
    fields: contractTemplateFields,
    submitFn: (data) => {
      return updateContractTemplate({ id: templateId, data }).unwrap();
    },
    redirectUrl: ContractTemplatesRoutes.root,
    title: 'Edytuj szablon kontraktu',
    icon: <EditIcon />,
    backUrl: ContractTemplatesRoutes.root,
    groupOrder: [FormRenderGroupTypes.basic, FormRenderGroupTypes.configuration],
    groupIcons: {
      Podstawowe: <InfoIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
      Konfiguracja: <SettingsIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    },
    showSuccessBeforeRedirect: true,
    redirectDelay: 2000,
    mutationStatus: updateStatus,
  });

  // Loading state
  if (isLoading) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Typography>
    );
  }

  // Error state
  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        Nie udało się załadować danych szablonu kontraktu.
      </Typography>
    );
  }

  // Not found state
  if (!contractTemplate) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        Szablon kontraktu nie został znaleziony.
      </Typography>
    );
  }

  return (
    <>
      <EntityFormLayout {...entityForm}>
        <AccordionCard
          title="Logo szablonu kontraktu"
          icon={<PhotoIcon fontSize="large" color="primary" />}
        >
          <UploadImagesForm
            onUploadSuccess={() => {
              // Po udanym uploadzie odśwież dane
              console.log('Logo uploaded successfully');
            }}
            useUploadMutation={() => [
              async (params: { contractTemplateId: number; files: File[] }) => {
                const result = await uploadLogo(params).unwrap();
                // Zaktualizuj logoUrl z wyniku uploadu
                if (result?.logoUrl) {
                  setLogoUrl(result.logoUrl);
                }
                return result;
              },
              uploadLogoStatus,
            ]}
            mutationParams={(files: File[]) => ({
              contractTemplateId: templateId,
              files,
            })}
          />

          {logoUrl && (
            <ContractTemplateLogoGallery
              logoUrl={logoUrl}
              onDelete={() => setLogoUrl('')}
              showDeleteButton={true}
            />
          )}
        </AccordionCard>
      </EntityFormLayout>
    </>
  );
};

export default EditContractTemplate;
